import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const BLENDER_TASKS: TutorialTask[] = [
  { 
    id: 'task-1',
    runId: '7b0114d4-cab4-46c7-9b64-ffda55e2e0a7', 
    name: 'mushroom hat material color', 
    type: 'success' 
  },
  { 
    id: 'task-2',
    runId: '9bb87256-c950-4033-996b-78b68b7e05e7', 
    name: 'add materials to raft and sea', 
    type: 'failure' 
  },
  { 
    id: 'task-3',
    runId: '9795e9a5-c306-4d9c-bb77-dd5d8fa9a150', 
    name: 'close all gaps', 
    type: 'fake_failure' 
  },
  { 
    id: 'task-4',
    runId: '401e9ee3-5afd-4625-aa3a-c27f78b6d350', 
    name: 'generated noise', 
    type: 'fake_failure' 
  },
  { 
    id: 'task-5',
    runId: 'fb3bea8c-205e-488d-bef4-6cbfd24e4521', 
    name: 'complete the armature 2', 
    type: 'fake_success' 
  },
  { 
    id: 'task-6',
    runId: '3fd7db18-f50f-4784-9e90-681dcfab09b2', 
    name: 'slow smoke simulation', 
    type: 'bad_task' 
  },
  { 
    id: 'task-7',
    runId: 'f0c24af3-bdfa-4cb4-8019-92b3c15ff29c', 
    name: 'move the non-spherical objects', 
    type: 'bad_task' 
  }
];

const BLENDER_STEPS: TutorialStep[] = [
  // Platform navigation instructions
  {
    id: "welcome",
    title: "Welcome to Blender Evaluation!",
    content:
      "This tutorial will show you how to evaluate Blender model runs. First, let's learn how to use the platform.",
    targetSelector: ".App-header h1",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-description",
    title: "Task Description",
    content:
      "This shows the instructions given to the AI model to complete inside Blender. Read this carefully to understand what the model was asked to do.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "skip-to-frame-2",
    title: "Skip to Frame 2",
    content:
      "Click the right arrow button or press the RIGHT arrow key to go to the next frame.",
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "current-action",
    title: "Current Action",
    content:
      "This section describes what action the AI model is performing in the current frame. It helps you understand what the model is trying to do.",
    targetSelector: ".sidebar-section:nth-child(3)",
    action: "next",
    position: "right",
  },
  {
    id: "main-click",
    title: "Main Click Indicator",
    content:
      "The circle with ripple effect shows where the model clicked on the screen in this frame. This helps you track the model's interactions.",
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "next-click",
    title: "Next Click Preview",
    content:
      "The faded circle shows where the model will click in the next frame. This helps you anticipate the model's next action.",
    targetSelector: ".next-click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "go-to-frame-3",
    title: "Go to Next Frame",
    content:
      'Click the right arrow button to advance to frame 3. Notice how the previous "next click" becomes the current click.',
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "seek-backward",
    title: "Previous Frame",
    content:
      "Click the left arrow button or press the LEFT arrow key to go back to the previous frame.",
    targetSelector: ".nav-button:first-child",
    action: "seek",
    position: "top",
  },
  {
    id: "play",
    title: "Play the Video",
    content: "Click the play button or press SPACE to start playing the video.",
    targetSelector: ".play-pause-btn",
    action: "play",
    position: "top",
  },
  {
    id: "pause",
    title: "Pause the Video",
    content: "Click the pause button or press SPACE to pause the video.",
    targetSelector: ".play-pause-btn",
    action: "pause",
    position: "top",
  },
  {
    id: "speed-controls",
    title: "Speed Controls",
    content:
      "Use the speed buttons to control playback speed. This helps you carefully review complex actions.",
    targetSelector: ".speed-selector",
    action: "next",
    position: "top",
  },
  {
    id: "seek-timeline",
    title: "Timeline Scrubbing",
    content:
      "Click and drag on the timeline to quickly navigate to any frame. The blue bar shows your current position. Try it now, then click Next when ready.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  {
    id: "download-model",
    title: "Download Blender File",
    content:
      "Right-click anywhere on the screenshot to download the Blender file. This is crucial for verifying results that may not be visible in the screenshot.",
    targetSelector: ".screenshot-container",
    action: "next",
    position: "left",
  },
  {
    id: "watch-before-eval",
    title: "Watch Before Evaluating",
    content:
      "Important: You must watch the entire video before evaluating. Let's jump to the final frame to see how evaluation works.",
    targetSelector: ".sidebar-section:nth-child(2)",
    action: "next",
    position: "right",
  },
  {
    id: "finalframe",
    title: "Jump to Final Frame",
    content:
      "Click on the timeline near the end to jump to the final frame. This shows the end result of the AI's work.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  // Task 1: Obvious Success
  {
    id: "task-1-intro",
    title: "Example 1: Obvious Success",
    content:
      "Let's start with an example of a clearly successful task completion. Watch how the AI changes the mushroom material color.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-1-watch",
    title: "Watch the AI Work",
    content:
      "Play the video or scrub through the timeline to see how the AI navigates to the Mix Color node and enters the exact hex value. Notice how the dots change to the specified color.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-1-evaluate",
    title: "Evaluate This Success",
    content:
      "Now that you've watched the AI complete the task successfully, click Success to mark this as a success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-1-complete",
    title: "Good Job!",
    content:
      'That\'s correct! The AI used the hex field as instructed, making it a clear success. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 2: Obvious Failure
  {
    id: "task-2-intro",
    title: "Example 2: Obvious Failure",
    content:
      "Now let's see a clear failure. The AI needs to assign materials to multiple elements but fails completely.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-2-watch",
    title: "Observe the Failure",
    content:
      "Watch the video to see how the AI struggles with multiple hex codes. Despite clear object names, it fails to assign even one correct material.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-2-evaluate",
    title: "Evaluate This Failure",
    content:
      "The complexity of multiple hex codes and the overall scence overwhelmed the AI. This is an obvious failure. Click Fail to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-2-complete",
    title: "Correct!",
    content:
      'You identified the failure correctly. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 3: False Failure (close all gaps)
  {
    id: "task-3-intro",
    title: "Example 3: False Failure",
    content:
      "This example shows why it's crucial to watch until completion. The AI makes a mistake but then corrects it.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-3-watch-correction",
    title: "Watch the Correction",
    content:
      "Play through the video carefully. The AI adds a Boolean modifier incorrectly, but then deletes it and adds it again properly. Don't judge too early!",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-3-evaluate",
    title: "Evaluate After Full Viewing",
    content:
      "The AI successfully corrected its mistake and achieved the intended result. This is actually a success. Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-3-complete",
    title: "Well Done!",
    content:
      'You correctly identified this as a success despite the initial error. Always watch to completion! Click "Next task".',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 4: False Failure (generated noise)
  {
    id: "task-4-intro",
    title: "Example 4: Hidden Success",
    content:
      "This task appears to fail but is actually successful. The issue is with viewport display settings.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-4-watch",
    title: "Watch and Notice",
    content:
      "Play the video and notice the final result doesn't show the expected displacement. But watch what mode the AI ends in.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-4-download-hint",
    title: "Download to Verify",
    content:
      "The displacement doesn't show because the AI ends in Edit mode. Right-click to download the file and then switch to Object mode. You'll notice that the modifier works correctly!",
    targetSelector: ".screenshot-container",
    action: "next",
    position: "left",
  },
  {
    id: "task-4-evaluate",
    title: "Evaluate Based on Task",
    content:
      "Since the task didn't specify avoiding Edit mode, and the modifier was added correctly, this is a success. Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-4-complete",
    title: "Excellent!",
    content:
      'You recognized that viewport settings don\'t indicate failure. The task was completed successfully. Click "Next task".',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 5: Fake Success
  {
    id: "task-5-intro",
    title: "Example 5: Deceptive Success",
    content:
      "This final example looks successful but actually fails. The AI appears to complete the parenting operation correctly.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-5-watch",
    title: "Watch Carefully",
    content:
      "Play through and watch the AI follow all the correct steps for parenting. It seems to do everything right.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-missing-confirmation",
    title: "Look for Confirmation",
    content:
      "But, something is suspicious. Notice how no objects remain selected and the parenting confirmation window is missing. Download the file to investigate further, and you'll see that the mesh doesn't follow the skeleton.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-evaluate",
    title: "Final Evaluation",
    content:
      "Despite following correct steps, the parenting failed. This is a failure. Click Fail.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-5-complete",
    title: "Good Analysis!",
    content:
      'You correctly identified the parenting failure. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 6: Bad Task - Slow smoke simulation
  {
    id: "task-6-intro",
    title: "Example 6: Ambiguous Animation Task",
    content:
      "This task asks the AI to create a 'slow' smoke simulation. Let's see why this is problematic.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-6-ambiguity",
    title: "Multiple Ambiguities",
    content:
      "The term 'slowly' has no specific values - what velocity? What time scale? 'Realistically' is subjective without density or turbulence parameters. 'Basic' doesn't define technical specs like voxel count or domain size.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-6-evaluate",
    title: "Mark as Bad Task",
    content:
      "This task has multiple interpretations and involves animation. Click Bad Task to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "bad-task",
  },
  {
    id: "task-6-complete",
    title: "Excellent!",
    content:
      'You identified the ambiguous animation task correctly. Click "Next task" for one more example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 7: Bad Task - Move non-spherical objects
  {
    id: "task-7-intro",
    title: "Example 7: Ambiguous Movement Task",
    content:
      "This task asks to move 'non-spherical objects by 3 units in the Z direction.' Watch for the ambiguities.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-7-coordinate-ambiguity",
    title: "Coordinate System Ambiguity",
    content:
      "Should objects move along the global Z-axis or their local Z-axes? With local coordinates selected, objects might scatter in different directions if they have different orientations.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-direction-ambiguity",
    title: "Direction Ambiguity",
    content:
      "The instruction says '3 units in the direction of the Z axis' but doesn't specify positive (+3, up) or negative (-3, down). The AI has a 50% chance of guessing wrong.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-evaluate",
    title: "Another Bad Task",
    content:
      "With multiple valid interpretations for both coordinate system and direction, this task is impossible to evaluate objectively. Click Bad Task.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "bad-task",
  },
  {
    id: "tutorial-complete",
    title: "Tutorial Complete!",
    content:
      "Congratulations! You've learned to identify obvious successes/failures, false failures, fake successes, and ambiguous bad tasks. Click below to start evaluating real Blender tasks. Click on 'New Projects' to claim your first labeling project.",
    action: "next",
    position: "bottom",
  },
];

export const blenderTutorialConfig: TutorialConfig = {
  software: 'blender',
  displayName: 'Blender',
  totalTasks: 7,
  tasks: BLENDER_TASKS,
  steps: BLENDER_STEPS,
  completionKey: 'blender_onboarding_v1_completed',
  dataSource: 'api',
  // No custom fetcher needed - will use standard API calls
};